import React, { useState, useEffect, useRef } from 'react';
import { MerchantStore, MerchantContainerInformation } from '../MerchantModel';
import i18n from '@/utils/i18n';
import { Communicator } from '../../../tools/Communication';
import { LayoutStoreInstance } from '../../../LayoutStore';
import { ContainerType } from '../../ContainerComponents/ContainerPage';
import { ContainerInformationForm } from '../ContainerInformationEdit';
import { EditItem, EditSection } from '../../../components/Layout';
import { currencyOfStoreQuery } from '../../../graphql/queries';
import { Currency, DEFAULT_CURRENCY } from '@/utils/price';
import client from '../../../apolloClientVytal';
import { ApolloQueryResult } from '@apollo/client';
import ClassicLoadingSpinner from '../../../components/LoadingSpinner/ClassicLoadingSpinner';
import { ContentSection } from '../../../components/Layout/ContentSection';

const getStoreForInfo = (
  vytalStoreGuid: string,
  externalPickpackId: string,
): MerchantStore => {
  return {
    id: vytalStoreGuid,
    merchantId: '',
    externalPickpackId: externalPickpackId,
    name: '',
    appUsers: [],
    storeManagers: [],
    imageIds: [],
    category: { id: 0, nameDe: '-', nameEn: '-' },
    openingHours: [],
    containerInformation: [],
    foodInfoTextDe: '',
    foodInfoTextEn: '',
    phoneNumber: '',
    websiteUrl: '',
    streetAddress: '',
    zipCode: '',
    city: '',
    country: '',
    latitude: 50.9278261,
    longitude: 6.9332776,
    contractedDate: '',
    onboardedDate: '',
    testPeriodEnd: '',
    sellsOfflineCards: false,
    amountOfflineCards: 0,
    region: 0,
  };
};

interface MerchantStorePageProps {
  vytalStoreGuid: string;
  externalPickpackId: string;
  title: string;
}

export const MerchantContainersTab: React.FC<MerchantStorePageProps> = ({
  vytalStoreGuid,
  externalPickpackId,
  title,
}) => {
  const saveMerchantPhoneInformationRef = useRef<(() => void) | null>(null);

  const [store, setStore] = useState<MerchantStore>(() =>
    getStoreForInfo(vytalStoreGuid, externalPickpackId),
  );
  const [isSaving, setIsSaving] = useState(false);
  const [containerTypes, setContainerTypes] = useState<ContainerType[]>([]);
  const [currency, setCurrency] = useState<string | null>(null);
  const [defaultInfo, setDefaultInfo] = useState<any>([]);
  const [finishedIntialLoad, setFinishedIntialLoad] = useState(false);

  const reloadMerchantStore = (storeId: string) => {
    return Communicator.Get('/Merchant/GetMerchantStore', { storeId })
      .then((response) => response.json())
      .then((store: MerchantStore) => {
        if (store) {
          setStore(store);
          if (store.externalPickpackId != null) {
            getCurrencyForStore(store.externalPickpackId)
              .then((queryResult) => {
                if (queryResult.errors == null) {
                  const currency = (Currency as any)[
                    queryResult.data.store.currency
                  ]; // make sure the currency is valid
                  if (currency == null && queryResult.data != null) {
                    console.error(
                      `Could not find currency: ${JSON.stringify(
                        queryResult.data,
                      )}`,
                    );
                  }
                  setCurrency(currency);
                } else {
                  console.error(
                    'Error getting currency of store: ',
                    queryResult.errors,
                  );
                }
              })
              .catch((reason) =>
                console.error('Error getting currency of store: ', reason),
              );
          }
        }
      });
  };

  const reloadContainerTypes = () => {
    return Communicator.Get('/Container/GetAllContainerCategories', {})
      .then((response) => response.json())
      .then((data: { containerTypes: ContainerType[] }) => {
        if (data.containerTypes) {
          setContainerTypes(
            [...data.containerTypes].sort((a, b) =>
              a.name.localeCompare(b.name, i18n.language, {
                numeric: true,
              }),
            ),
          );
        }
      });
  };

  const onSaveClick = async (lowerUpperContainerInformation: any[] = []) => {
    setIsSaving(true);
    await Promise.all([
      saveStoreVytalInfoOnly(lowerUpperContainerInformation),
      // @ts-ignore
      saveMerchantPhoneInformationRef.current?.(),
    ]);

    setIsSaving(false);
  };

  const getDefaultContainerInfo = (containerTypeId: number) => {
    let currencyToUse = currency || DEFAULT_CURRENCY;
    let entry = defaultInfo.find((info: any) => info.Id === containerTypeId);

    if (entry == null || entry[currencyToUse] == null) {
      console.error(
        'Container type id or currency could not be found in default settings:',
        entry,
        entry && entry[currencyToUse],
        currencyToUse,
      );

      if (currencyToUse == null) {
        currencyToUse = DEFAULT_CURRENCY;
      }

      if (entry == null) {
        entry = {
          [currencyToUse]: {
            TransactionPrice: 0.2,
            StoreLostCost: 1000,
            StoreLostCostUpper: 240,
            StoreLostCostLower: 600,
          },
        };
      } else {
        entry[currencyToUse] = {
          TransactionPrice: 0.2,
          StoreLostCost: 1000,
          StoreLostCostUpper: 240,
          StoreLostCostLower: 600,
        };
      }
    }

    return entry[currencyToUse];
  };

  const getDefaultStoreLostCostForContainerTypeId = (
    containerTypeId: number,
  ) => {
    return getDefaultContainerInfo(containerTypeId)['StoreLostCost'];
  };

  const getDefaultTransactionPriceForContainerTypeId = (
    containerTypeId: number,
  ) => {
    return getDefaultContainerInfo(containerTypeId)['TransactionPrice'];
  };

  const getDefaultStoreLostCostUpperForContainerTypeId = (
    containerTypeId: number,
  ) => {
    return getDefaultContainerInfo(containerTypeId)['StoreLostCostUpper'];
  };

  const getDefaultStoreLostCostLowerForContainerTypeId = (
    containerTypeId: number,
  ) => {
    return getDefaultContainerInfo(containerTypeId)['StoreLostCostLower'];
  };

  const saveStoreVytalInfoOnly = (
    lowerUpperContainerInformation: any[] = [],
  ) => {
    return new Promise((resolve, reject) => {
      const data = {
        id: store.id,
        categoryId: store.category.id,
        containerInformation: store.containerInformation,
      };

      if (lowerUpperContainerInformation.length > 0) {
        for (const containerInfo of data.containerInformation) {
          const containerInfo2 = lowerUpperContainerInformation.find(
            (i) => i.containerTypeId == containerInfo.containerTypeId,
          );
          if (containerInfo2 != null) {
            containerInfo.storeLostCostLower =
              containerInfo2.storeLostCostLower;
            containerInfo.storeLostCostUpper =
              containerInfo2.storeLostCostUpper;
          }
        }
      }

      const convertContainerInfoInput = (
        input: string | null | undefined | number,
      ) => (typeof input === 'string' ? parseInt(input) : input);

      data.containerInformation = data.containerInformation.map(
        (containerInfo) => ({
          ...containerInfo,
          // when chaning the prices they are saved as strings => transfer them to numbers
          transactionPrice: parseFloat(
            String(containerInfo.transactionPrice) || '0',
          ),
          storeLostCost:
            convertContainerInfoInput(containerInfo.storeLostCost) || 0,
          storeLostCostUpper:
            convertContainerInfoInput(containerInfo.storeLostCostUpper) || 0,
          storeLostCostLower:
            convertContainerInfoInput(containerInfo.storeLostCostLower) || 0,
        }),
      );

      Communicator.PostData('/Merchant/UpdateMerchantStore', data)
        .then((data: any) => {
          LayoutStoreInstance.setSuccess(i18n.t('Merchants:storeSaved'));
          resolve(data);
        })
        .catch(() => {
          LayoutStoreInstance.setError(i18n.t('Merchants:errorSavingStore'));
          reject();
        });
    });
  };

  const getDefaultContainerInformation = (
    containerTpyeId: number,
  ): MerchantContainerInformation => {
    return {
      storeId: store.id,
      containerTypeId: containerTpyeId,
      transactionPrice:
        getDefaultTransactionPriceForContainerTypeId(containerTpyeId),
      storeLostCost: getDefaultStoreLostCostForContainerTypeId(containerTpyeId),
      storeLostCostLower:
        getDefaultStoreLostCostLowerForContainerTypeId(containerTpyeId),
      storeLostCostUpper:
        getDefaultStoreLostCostUpperForContainerTypeId(containerTpyeId),
      isSelected: false,
      isAccepted: false,
    };
  };

  const resetContainerInformationToDefault = (all: boolean = false) => {
    const newContainerInfos: MerchantContainerInformation[] = [];
    for (const containerInfo of store.containerInformation) {
      if (all || containerInfo.isSelected) {
        containerInfo.transactionPrice =
          getDefaultTransactionPriceForContainerTypeId(
            containerInfo.containerTypeId,
          );
        containerInfo.storeLostCost = getDefaultStoreLostCostForContainerTypeId(
          containerInfo.containerTypeId,
        );
        containerInfo.storeLostCostLower =
          getDefaultStoreLostCostLowerForContainerTypeId(
            containerInfo.containerTypeId,
          );
        containerInfo.storeLostCostUpper =
          getDefaultStoreLostCostUpperForContainerTypeId(
            containerInfo.containerTypeId,
          );
        containerInfo.isSelected = false;
      }
      newContainerInfos.push(containerInfo);
    }
    setStore({ ...store, containerInformation: newContainerInfos });
    onSaveClick();
  };

  const getContainerInformation = (
    containerTypeId: number,
    containerInformationArray = store.containerInformation,
  ) => {
    let res: MerchantContainerInformation | undefined =
      containerInformationArray.find((_) =>
        _.containerTypeId == containerTypeId ? containerTypeId : 0,
      );
    if (res == null) {
      res = getDefaultContainerInformation(containerTypeId);
    }
    return res;
  };

  const updateContainerInformation = (
    containerTypeId: number,
    update: object,
  ) => {
    const newContainerInfos = [...store.containerInformation];
    const index = newContainerInfos.findIndex(
      (_) => _.containerTypeId === containerTypeId,
    );
    if (index === -1) {
      throw new Error(`Invalid container type id: ${containerTypeId}`);
    } else {
      newContainerInfos[index] = {
        ...newContainerInfos[index],
        ...update,
      };
    }
    setStore({ ...store, containerInformation: newContainerInfos });
  };

  const getCurrencyForStore = (
    pickpackId: string,
  ): Promise<ApolloQueryResult<{ store: { currency: string } }>> => {
    return client.query({
      query: currencyOfStoreQuery,
      variables: { id: pickpackId },
      fetchPolicy: 'network-only',
    });
  };

  const getDefaultContainerInformationFromDb = () => {
    return Communicator.Get('/Config/GetConfigValue', {
      key: 'DefaultContainerInformation',
    })
      .then((response) => response.json())
      .then((defaultInfo) => setDefaultInfo(defaultInfo));
  };

  // Effect for initial component mount
  useEffect(() => {
    Promise.all([
      getDefaultContainerInformationFromDb(),
      reloadMerchantStore(vytalStoreGuid),
      reloadContainerTypes(),
    ]).then(() => setFinishedIntialLoad(true));
  }, []);

  // Effect for when props change (equivalent to componentWillUpdate)
  useEffect(() => {
    setStore(getStoreForInfo(vytalStoreGuid, externalPickpackId));
    reloadMerchantStore(vytalStoreGuid);
  }, [vytalStoreGuid, externalPickpackId]);

  if (!finishedIntialLoad) {
    return <ClassicLoadingSpinner></ClassicLoadingSpinner>;
  }

  return (
    <ContentSection title={title} collapsable={false} key="content">
      <EditSection
        title=""
        isLoading={isSaving}
        onSave={onSaveClick.bind(null)}
      >
        <EditItem title={i18n.t('Navigation:containers')}>
          <ContainerInformationForm
            storeId={store.id}
            containerTypes={containerTypes}
            getContainerInformation={getContainerInformation}
            updateContainerInformation={updateContainerInformation}
            currency={currency}
            resetContainerInformation={resetContainerInformationToDefault}
            saveContainerInformation={onSaveClick}
            country={store.country}
          />
        </EditItem>
      </EditSection>
    </ContentSection>
  );
};
